import React, { useRef, createRef, useState, useEffect } from 'react';

import styled from 'styled-components';
import {Redirect} from 'react-router-dom';
import { AssessmentSchemaProps } from '../../common/mongoSchemas';
import { spacing } from '@material-ui/system';
import { firebaseStorage } from '../../firebaseConfig';
import { debugLog } from '../../common/debugUtilities';

import {
  Card as MuiCard,
  CardContent as MuiCardContent,
  IconButton,
  LinearProgress,
  Menu,
  MenuItem,
  Paper as MuiPaper,
  Table,
  TableBody,
  TableCell,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
  Snackbar
} from '@material-ui/core';

import {
  Delete,
  VideocamTwoTone,
  CloudDownload,
  AddCircleOutlined,
  MoreVert,
  ChevronRight
} from '@material-ui/icons';

import RestartAltTwoToneIcon from '@mui/icons-material/RestartAltTwoTone';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { showAlert } from '../../common/utilities';
import AssessmentTwoToneIcon from '@mui/icons-material/AssessmentTwoTone';

import AssessmentTableHead from './AssessmentTableHead';
import { useUserState, refreshToken} from '../../context/UserContext';
import { stableSort, getSorting } from '../../common/tableUtilities';
import VideoDetails from '../../dialogs/VideoDetails/VideoDetails';
import ReportMetricsGrid from '../../dialogs/VideoDetails/components/ReportMetricsGrid.js';
import ClinicianReportGrid from '../../dialogs/VideoDetails/components/ClinicianReportGrid.js';
import ExplainableReport from '../../dialogs/VideoDetails/components/ExplainableReport.js';
import AssessmentTableFilter from './AssessmentTableFilter';
import AssessmentDetailsPopup from './AssessmentDetailsPopup';
import { deleteCloudVideo, reprocessVideo, showDialog } from '../../common/utilities';

import { useTranslation, withTranslation } from 'react-i18next';

const CardContent = styled(MuiCardContent)(spacing);
const Card = styled(MuiCard)(spacing);
const Paper = styled(MuiPaper)(spacing);

const TableContainer = styled.div`
  position: relative;
  max-width: calc(100vw - 12px);
`;

const TableWrapper = styled.div`
  overflow-x: auto;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch; /* Smooth scrolling on iOS */

  /* Subtle scrollbar styling for better UX */
  &::-webkit-scrollbar {
    height: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #555;
  }
`;

const ScrollIndicator = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  width: 60px;
  background: linear-gradient(to left, rgba(255,255,255,0.95), transparent);
  pointer-events: none;
  z-index: 1;
  display: ${props => props.show ? 'flex' : 'none'};
  align-items: center;
  justify-content: flex-end;
  padding-right: 8px;

  @media (min-width: 960px) {
    display: none; /* Hide on larger screens where all columns fit */
  }
`;

const ChevronIndicator = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: #666;
  opacity: ${props => props.hasScrolled ? 0.3 : 1};
  transition: opacity 0.5s ease;

  /* Pulsing animation */
  animation: ${props => props.hasScrolled ? 'none' : 'pulse 2s ease-in-out infinite'};

  @keyframes pulse {
    0%, 100% {
      transform: translateX(0);
      opacity: 0.6;
    }
    50% {
      transform: translateX(-4px);
      opacity: 1;
    }
  }
`;

const AssessmentInfoTable = (props) => {

const {t, i18n} = useTranslation();
const user = useUserState();
const {allAssessments, handleFilter, handleResetFilter, handleRestart, handleRestartSurprise, handleDelete} = props;

const [order, setOrder] = useState('desc');
const [orderBy, setOrderBy] = useState('parent');
const [page, setPage] = useState(0);
const [rowsPerPage, setRowsPerPage] = useState(5); // 5 entries per page by default
const detailsPageElement = useRef();
const [openReport, setOpenReport] = useState(false);
const [currentAssessment, setCurrentAssessment] = useState(null);
const [menuAnchorEl, setMenuAnchorEl] = useState(null);
const [selectedAssessment, setSelectedAssessment] = useState(null);
const [showScrollIndicator, setShowScrollIndicator] = useState(false);
const [hasScrolled, setHasScrolled] = useState(false);
const tableWrapperRef = useRef(null);
const [detailsPopupOpen, setDetailsPopupOpen] = useState(false);
const [selectedAssessmentId, setSelectedAssessmentId] = useState(null);

  const emptyRows =
  rowsPerPage - Math.min(rowsPerPage, allAssessments?.length - page * rowsPerPage);

  const handleRequestSort = (event, property) => {
    const orderByProp = property;
    let orderProp = 'desc';

    if (orderBy === property && order === 'desc') {
      orderProp = 'asc';
    }
    setOrder(orderProp);
    setOrderBy(orderByProp);
  };

  const handleChangePage = (event, page) => {
    setPage(page);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(event.target.value);
  };

  const handleVideoDetails = async(asmt) => {
    if (!detailsPageElement.current) {
      console.error('VideoDetails ref not initialized');
      return;
    }
    detailsPageElement.current.setVideoData(asmt, user);
    await refreshToken(user);
    detailsPageElement.current.handleClickOpen();

  };



  const reportDialogComplete = ()=> {
    setOpenReport(false);
    setCurrentAssessment(null);
  }

  const handleReportDetails = async (asmt) => {
    setCurrentAssessment(asmt);
    if(asmt?.notes){
        setOpenReport(true);
    }
    else{
        await showAlert('Report', 'There is no report for this video yet!', 'Ok');
    }

  }

  // Detect horizontal overflow and handle scroll indicator visibility
  useEffect(() => {
    const checkOverflow = () => {
      if (tableWrapperRef.current) {
        const hasOverflow = tableWrapperRef.current.scrollWidth > tableWrapperRef.current.clientWidth;
        const isScrolledToEnd = tableWrapperRef.current.scrollLeft + tableWrapperRef.current.clientWidth >= tableWrapperRef.current.scrollWidth - 5;
        setShowScrollIndicator(hasOverflow && !isScrolledToEnd);

        // Mark as scrolled if user has scrolled horizontally at all
        if (tableWrapperRef.current.scrollLeft > 0) {
          setHasScrolled(true);
        }
      }
    };

    checkOverflow();

    const handleScroll = () => checkOverflow();
    const wrapper = tableWrapperRef.current;
    if (wrapper) {
      wrapper.addEventListener('scroll', handleScroll);
    }

    window.addEventListener('resize', checkOverflow);

    return () => {
      if (wrapper) {
        wrapper.removeEventListener('scroll', handleScroll);
      }
      window.removeEventListener('resize', checkOverflow);
    };
  }, [allAssessments, page, rowsPerPage]);

  const handleMenuOpen = (event, assessment) => {
    setMenuAnchorEl(event.currentTarget);
    setSelectedAssessment(assessment);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedAssessment(null);
  };

  const handleMenuRestart = () => {
    if (selectedAssessment) {
      handleRestart(selectedAssessment);
    }
    handleMenuClose();
  };

  const handleMenuRestartSurprise = () => {
    if (selectedAssessment && handleRestartSurprise) {
      handleRestartSurprise(selectedAssessment);
    }
    handleMenuClose();
  };

  const handleMenuDelete = () => {
    if (selectedAssessment) {
      handleDelete(selectedAssessment);
    }
    handleMenuClose();
  };


    if (user.authLevel < 0){
    return (
        <Typography variant="h5">
        Hmmm...you may not yet have content here.
        </Typography>
    );
    }
    return (
        <Paper>
        <VideoDetails ref={detailsPageElement} user={user}/>
        {user.authLevel > 0 &&
          <AssessmentTableFilter
            handleFilter={handleFilter}
            handleResetFilter={handleResetFilter}/>}
        <TableContainer>
        <ScrollIndicator show={showScrollIndicator}>
          <ChevronIndicator hasScrolled={hasScrolled}>
            <ChevronRight fontSize="large" />
          </ChevronIndicator>
        </ScrollIndicator>
        <TableWrapper ref={tableWrapperRef}>
            <Table aria-labelledby="tableTitle">
                <AssessmentTableHead
                // numSelected={selectedVideoIds.length}
                onRequestSort={handleRequestSort} 
                order={order}
                orderBy={orderBy}
                rowCount={allAssessments.length}
                />
                <TableBody>
                {allAssessments &&

                stableSort(allAssessments, getSorting(order, orderBy))
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map(assessment => {
                    const idToUse = assessment._id;
                    return (
                        <TableRow
                            hover
                            key={idToUse}
                            onClick = {(e)=>{
                                // Don't open popup if clicking on action buttons
                                if (e.target.closest('button') || e.target.closest('svg')) {
                                    return;
                                }
                                debugLog('Clicked row:', idToUse);
                                setSelectedAssessmentId(idToUse);
                                setDetailsPopupOpen(true);
                            }}
                            style={{cursor: 'pointer'}}
                            // selected={isSelected}
                            tabIndex={-1}
                        >
                            <TableCell
                            align="center"
                            style={{
                              width:'10%',
                              position: 'sticky',
                              left: 0,
                              backgroundColor: '#fafafa',
                              zIndex: 2,
                              boxShadow: '2px 0 4px rgba(0,0,0,0.1)',
                              borderRight: '2px solid #e0e0e0',
                              fontWeight: 500
                            }}
                            >
                                {assessment?.date &&
                                (i18n.language == 'es' || i18n.language == 'es-ES') ?
                                (
                                  new Date(assessment.date).toLocaleDateString('es-ES', { year: 'numeric', month: '2-digit', day: '2-digit' })
                                ):
                                (
                                  new Date(assessment.date).toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' })
                                )
                                }

                            </TableCell>

                            <TableCell 
                            align="left"
                            style={{width:'10%'}}
                            >
                            {/* This logic is geared towards assessments being NEW and being processed
                            only the first  
                            We also have to account for assessments being restarted for AI processing 
                            In this case, resulturl will already be filled up by the previous processing 
                            But we still would like to show the progress bar until the new processing is complete
                            Oct 2024 */}
                            {

                            assessment?.status == "complete" || assessment?.resulturl? (
                                <div> {t('completed',{ns:'asmttablehead'})} </div>
                            ): ( <div>
                                {assessment?.percentageCompleted  ?
                                (
                                    <div>
                                        {assessment?.stage && (
                                            <div style={{fontSize: '0.75rem', marginBottom: '4px', color: '#666'}}>
                                                {assessment.stage}
                                            </div>
                                        )}
                                        <LinearProgress
                                        variant="determinate"
                                        value={assessment.percentageCompleted}/>
                                    </div>
                                ):
                                (
                                    <div>
                                        {t('inq',{ns:'asmttablehead'})}
                                    </div>

                                )
                                }
                                </div>
                            )

                            }
                            </TableCell>
                            
                            <TableCell 
                            align="left"
                            style={{width:'10%'}}
                            >
                                <IconButton color="primary"
                                    onClick={()=>{
                                      debugLog('Report for: ', assessment);
                                      handleReportDetails(assessment)}}>
                                    <AssessmentTwoToneIcon/>
                                </IconButton>
                            </TableCell>
                            
                            <TableCell 
                            align="left"
                            style={{width:'10%'}}
                            >
                                <IconButton color="primary" onClick={()=>{handleVideoDetails(assessment)}}>
                                    <VideocamTwoTone/>
                                </IconButton>
                            </TableCell>
                            {  user.authLevel >= 0 &&
                            <TableCell
                            align="left"
                            style={{width:'10%'}}
                            >
                                <IconButton
                                 color="primary"
                                 onClick={(e)=>{
                                  debugLog('More options click');
                                  handleMenuOpen(e, assessment);
                                  }}>
                                    <MoreVert/>
                                </IconButton>
                            </TableCell>
                            }

                        </TableRow>
                    );
                })}
                {emptyRows > 0 && (
                  <TableRow style={{ height: 49 * emptyRows }}>
                    <TableCell colSpan={6} />
                  </TableRow>
                )}
                </TableBody>
            </Table>
        </TableWrapper>
        </TableContainer>

        <TablePagination
          backIconButtonProps={{
            'aria-label': 'Previous Page'
          }}
          component="div"
          count={allAssessments.length}
          nextIconButtonProps={{
            'aria-label': 'Next Page'
          }}
          onChangePage={handleChangePage}
          onChangeRowsPerPage={handleChangeRowsPerPage}
          page={page}
          rowsPerPage={rowsPerPage}
          labelRowsPerPage={t('rowsperpage',{ns:'asmttablehead'})}
          rowsPerPageOptions={[5, 10, 25]}
        />
        {/* Role-based report rendering: Clinician (admin) vs Parent */}
        {user.authLevel > 0 ? (
          <ExplainableReport isOpen={openReport} assessment={currentAssessment} dialogComplete={reportDialogComplete}/>
        ) : (
          <ReportMetricsGrid isOpen={openReport} assessment={currentAssessment} dialogComplete={reportDialogComplete}/>
        )}

        <Menu
          anchorEl={menuAnchorEl}
          open={Boolean(menuAnchorEl)}
          onClose={handleMenuClose}
          PaperProps={{
            style: {
              boxShadow: '0px 2px 8px rgba(0,0,0,0.15)',
            },
          }}
        >
          {user.authLevel > 0 && (
            <MenuItem onClick={handleMenuRestart}>
              <RestartAltTwoToneIcon style={{ marginRight: 8 }} fontSize="small" />
              {t('restart', {ns: 'asmttablehead'}) || 'Restart Processing'}
            </MenuItem>
          )}
          {user.authLevel > 0 && (
            <MenuItem onClick={handleMenuRestartSurprise}>
              <RefreshIcon style={{ marginRight: 8 }} fontSize="small" />
              {t('restartsurprise', {ns: 'asmttablehead'}) || 'Restart Surprise'}
            </MenuItem>
          )}
          <MenuItem onClick={handleMenuDelete} style={{ color: '#d32f2f' }}>
            <DeleteForeverIcon style={{ marginRight: 8 }} fontSize="small" />
            {t('delete', {ns: 'asmttablehead'}) || 'Delete'}
          </MenuItem>
        </Menu>

        {/* Assessment Details Popup */}
        <AssessmentDetailsPopup
          open={detailsPopupOpen}
          assessmentId={selectedAssessmentId}
          onClose={() => setDetailsPopupOpen(false)}
        />

        </Paper>
    );
};

export default AssessmentInfoTable;
